<template>
    <div class="capacity-board">
        <!-- 热力图段（独立白底卡） -->
        <div class="bg-white pt10 pl10 pr10 pb10 borderRadius5 mb10">
            <div class="hm-head">
                <span class="hm-title">产能水位看板</span>
                <!-- 2026-10-21：展示窗口可切换 7/14/20 个工作日（纯前端：宽区间取数后截取前 N 个工作日） -->
                <el-radio-group v-model="daysCount" size="mini" class="hm-days" @change="loadAll">
                    <el-radio-button :label="7">7 个工作日</el-radio-button>
                    <el-radio-button :label="14">14 个工作日</el-radio-button>
                    <el-radio-button :label="20">20 个工作日</el-radio-button>
                </el-radio-group>
                <span class="hm-sub" v-if="!isOps">行=组 · 列=工作日 · 格=已排/总产能；大类行含当日扩充。</span>
                <span class="hm-legend">
                    <span class="hm-lg green">绿 &lt;60%（充足）</span>
                    <span class="hm-lg yellow">黄 60–80%</span>
                    <span class="hm-lg red">红 ≥80%</span>
                </span>
            </div>

            <div v-if="!cats.length" class="hm-empty">暂无产能配置或排单数据</div>

            <!-- 逐大类一张热力表（动态列逐列 v-if + :key 强制重建，避免列错位） -->
            <div v-for="cat in cats" :key="renderKey + '_' + cat.category_group_id" class="hm-cat">
                <div class="hm-cat-head">{{ cat.category_group_name }}</div>
                <div class="hm-scroll">
                    <table class="hm-table">
                        <thead>
                            <tr>
                                <th class="hm-grp-th">{{ isOps ? '大类 ＼ 日期' : '组 ＼ 日期' }}</th>
                                <th v-for="(d, di) in days" :key="'h_' + cat.category_group_id + '_' + di">
                                    <div class="hm-date">{{ d.slice(5) }}</div>
                                    <div class="hm-dow">周{{ dowText(d) }}</div>
                                    <div class="hm-ex" v-if="!isOps && catExpand(cat, d) > 0">+{{ catExpand(cat, d) }}扩充</div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <!-- 运营脱敏：单行大类整体，仅百分比 -->
                            <template v-if="isOps">
                                <tr>
                                    <td class="hm-grp"><b>{{ cat.category_group_name }}（整体）</b></td>
                                    <td v-for="(cell, ci) in cat.days" :key="'oc_' + cat.category_group_id + '_' + ci"
                                        class="hm-cell" :class="sigClass(cell.signal)"
                                        :title="cat.category_group_name + ' ' + cell.date + '：水位 ' + pct(cell.ratio) + '%'">
                                        <div class="hm-pct">{{ pct(cell.ratio) }}%</div>
                                    </td>
                                </tr>
                            </template>
                            <!-- 创意全量：大类行 + 各叶组行（已排/上限 + 百分比） -->
                            <template v-else>
                                <tr class="hm-cat-row">
                                    <td class="hm-grp"><b>{{ cat.category_group_name }}（整体）</b></td>
                                    <td v-for="(cell, ci) in cat.days" :key="'fc_' + cat.category_group_id + '_' + ci"
                                        class="hm-cell" :class="sigClass(cell.signal)"
                                        :title="cell.date + '：已排 ' + cell.used + ' / 总产能 ' + cell.cap + (cell.expand ? '（含扩充 ' + cell.expand + '）' : '')">
                                        <div class="hm-used">{{ cell.used }}<span class="hm-lim">/{{ cell.cap }}</span><span v-if="cell.expand > 0" class="hm-expand" title="当日扩充">+{{ cell.expand }}</span></div>
                                        <div class="hm-pct">{{ pct(cell.ratio) }}%</div>
                                    </td>
                                </tr>
                                <tr v-for="g in cat.groups" :key="'g_' + g.group_id">
                                    <td class="hm-grp hm-grp-leaf">{{ g.group_name }}</td>
                                    <td v-for="(cell, ci) in g.days" :key="'gc_' + g.group_id + '_' + ci"
                                        class="hm-cell" :class="sigClass(cell.signal)"
                                        :title="g.group_name + ' ' + cell.date + '：已排 ' + cell.used + ' / 总产能 ' + cell.cap">
                                        <div class="hm-used">{{ cell.used }}<span class="hm-lim">/{{ cell.cap }}</span><span v-if="cell.expand > 0" class="hm-expand" title="当日扩充">+{{ cell.expand }}</span></div>
                                        <div class="hm-pct">{{ pct(cell.ratio) }}%</div>
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- 渠道占比 vs 实际派单对比段（运营 isOps 整段隐藏·仅创意全量可见·2026-06-29） -->
        <div class="bg-white pt10 pl10 pr10 pb10 borderRadius5" v-if="!isOps">
            <div class="hm-head">
                <span class="hm-title">渠道占比 vs 实际派单</span>
                <span class="hm-sub" v-if="isOps">配置占比与实际派单占比对比（仅百分比）。</span>
                <span class="hm-sub" v-else>配置占比 vs 实际派单（实际=Σ需求数·全部已排）；渠道行占比相对大类组，展开的档行占比相对该渠道。</span>
            </div>
            <div v-if="!channelCats.length" class="hm-empty">暂无渠道占比配置或派单数据</div>
            <div v-for="cc in channelCats" :key="'ch_' + renderKey + '_' + cc.category_group_id" class="ch-cat">
                <div class="ch-cat-head">
                    {{ cc.category_group_name }}
                    <span class="ch-total" v-if="!isOps && cc.actual_total !== undefined">实际派单总量 {{ cc.actual_total }}</span>
                    <span class="ch-toggle-all" v-if="catTierChannels(cc).length" @click="toggleCatAll(cc)">
                        {{ isCatAllOpen(cc) ? '收起全部档' : '展开全部档' }}
                    </span>
                </div>
                <table class="ch-table">
                    <thead>
                        <tr>
                            <th>渠道 / 档</th>
                            <th>配置占比</th>
                            <th>实际派单占比</th>
                            <th v-if="!isOps">实际派单量</th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-for="ch in cc.channels">
                            <!-- 渠道行：配置=根行 ratio；实际=该渠道 ÷ 大类组总量。有档才可展开 -->
                            <tr :key="'chr_' + cc.category_group_id + '_' + ch.plat_id"
                                class="ch-row" :class="{ 'ch-row-open': isChannelOpen(cc, ch), 'ch-row-expandable': tierList(ch).length }"
                                @click="toggleChannel(cc, ch)">
                                <td>
                                    <i class="ch-caret" :class="tierList(ch).length
                                        ? (isChannelOpen(cc, ch) ? 'el-icon-caret-bottom' : 'el-icon-caret-right')
                                        : 'ch-caret-none'"></i>
                                    <span class="ch-name">{{ ch.plat_name }}</span>
                                    <span v-if="tierList(ch).length" class="ch-tier-count">{{ tierList(ch).length }} 档</span>
                                    <!-- 配置缺口：有实际派单却不在占比配置里，大类合计 100% 不含它 → 必须去补 -->
                                    <span v-else-if="showUnconfigured(ch)" class="ch-flag danger"
                                        title="该渠道有实际派单，但未配置渠道占比，大类占比合计不含它 —— 请到「产能配置 → 渠道占比」补充">未配置占比</span>
                                    <!-- 配置合法且完整：「只加渠道不打标」是设计允许的路径，细分档位只是可选项 -->
                                    <span v-else-if="showUntagged(ch)" class="ch-flag warn"
                                        title="该渠道占比配置完整，只是未细分档位（产品类型 × 包体类型 × 游戏类型）；如需看档级明细，可到「产能配置 → 渠道占比」为该渠道细分（可选）">未打标</span>
                                </td>
                                <td>
                                    <div class="ratio-bar"><span class="ratio-fill conf" :style="{ width: barW(ch.config_ratio) }"></span></div>
                                    <span class="ratio-num">{{ fmtRatio(ch.config_ratio) }}%</span>
                                </td>
                                <td>
                                    <div class="ratio-bar"><span class="ratio-fill act" :style="{ width: barW(ch.actual_ratio) }"></span></div>
                                    <span class="ratio-num" :title="chActualTitle(cc, ch)">{{ fmtRatio(ch.actual_ratio) }}%</span>
                                </td>
                                <td v-if="!isOps">{{ ch.actual_num }}</td>
                            </tr>
                            <!-- 档级行：配置=叶行 ratio（渠道内相对）；实际=该档 ÷ 该渠道总数。未归类无配置显 — -->
                            <tr v-for="t in (isChannelOpen(cc, ch) ? tierList(ch) : [])"
                                :key="'cht_' + cc.category_group_id + '_' + ch.plat_id + '_' + t.key"
                                class="ch-tier-row">
                                <td class="ch-tier-name">{{ t.tier_name }}</td>
                                <td>
                                    <template v-if="hasConf(t.config_ratio)">
                                        <div class="ratio-bar sm"><span class="ratio-fill conf" :style="{ width: barW(t.config_ratio) }"></span></div>
                                        <span class="ratio-num" :title="tierConfTitle(ch, t)">{{ fmtRatio(t.config_ratio) }}%</span>
                                    </template>
                                    <span v-else class="ratio-na" title="未归类派单没有对应的配置占比">—</span>
                                </td>
                                <td>
                                    <div class="ratio-bar sm"><span class="ratio-fill act" :style="{ width: barW(t.actual_ratio) }"></span></div>
                                    <span class="ratio-num" :title="tierActualTitle(ch, t)">{{ fmtRatio(t.actual_ratio) }}%</span>
                                </td>
                                <td v-if="!isOps">{{ t.actual_num }}</td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
import API from '@/api/workOrder/capacityBoard.js'
import { debounce } from 'lodash'

export default {
    name: 'CapacityBoard',
    // 父页(dashboard/index.vue)统一查询条 form；本看板不做筛选(阶段10 去查询区·后端固定默认工作日)，
    // 仅借 form 变化作「点查询/刷新」信号重取最新水位(排单后水位变化靠此触达)·BUG-002-AA
    props: { form: { type: Object, default: () => ({}) } },
    data() {
        return {
            // 热力图：展示窗口 7/14/20 个工作日（2026-10-21 可切换；7 用后端默认，14/20 前端宽区间取数后截取）
            daysCount: 7,
            view: 'full',
            days: [],
            cats: [],
            // 渠道对比
            channelCats: [],
            // 渠道行展开态（渠道占比多维 Phase A）：key = `${大类组id}_${plat_id}` → true 展开
            // 默认全收起，重新拉数时清空（渠道/档集合可能已变，保留旧态会错位）
            expandedChannels: {},
            renderKey: 0, // 切换/查询时 +1，强制重建动态列
        }
    },
    computed: {
        // 视图由接口返回的 view 字段决定（接口层脱敏，非前端隐藏）
        isOps() {
            return this.view === 'ops'
        },
    },
    watch: {
        // 点查询(父重置 form) / 首次挂载(immediate) → 重取；deep 兼容嵌套数组筛选变化
        form: {
            handler: debounce(function () {
                this.loadAll()
            }, 500),
            deep: true,
            immediate: true,
        },
    },
    methods: {
        async loadAll() {
            // 渠道对比要用热力图截取后的首尾工作日，所以串行
            await this.loadHeatmap()
            await this.loadChannel()
            this.renderKey++
        },
        /** 本地日期 Y-m-d */
        dateStr(d) {
            const p = (n) => (n < 10 ? '0' + n : '' + n)
            return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate())
        },
        /** 日期加 N 个自然日，返回 Y-m-d */
        addDays(ymd, n) {
            const d = new Date(ymd + 'T00:00:00')
            d.setDate(d.getDate() + n)
            return this.dateStr(d)
        },
        async loadHeatmap() {
            const n = this.daysCount
            if (n === 7) {
                // 7 天直接用后端默认窗口（默认 7 个工作日）
                const { data } = await API.getHeatmap({})
                this.view = data.view || 'full'
                this.days = data.days || []
                this.cats = data.cats || []
                return
            }
            // 14/20：前端无节假日表，先向后端要一个足够宽的自然日区间（按 n*3+14 兜住周末/连假），
            // 再截取前 n 个工作日；不够时逐步扩大区间重试。后端只做 3 条聚合 SQL，宽区间成本可控。
            const from = this.dateStr(new Date())
            let span = n * 3 + 14
            let data = (await API.getHeatmap({ date_from: from, date_to: this.addDays(from, span) })).data
            let allDays = data.days || []
            while (allDays.length < n && span < 180) {
                span += 30
                data = (await API.getHeatmap({ date_from: from, date_to: this.addDays(from, span) })).data
                allDays = data.days || []
            }
            const keepDays = allDays.slice(0, n)
            const keepSet = {}
            keepDays.forEach((d) => { keepSet[d] = true })
            this.view = data.view || 'full'
            this.days = keepDays
            // 大类行 / 叶组行按同一批日期截断，避免列数与表头错位
            this.cats = (data.cats || []).map((cat) => {
                cat.days = (cat.days || []).filter((x) => keepSet[x.date])
                cat.groups = (cat.groups || []).map((g) => {
                    g.days = (g.days || []).filter((x) => keepSet[x.date])
                    return g
                })
                return cat
            })
        },
        async loadChannel() {
            // 渠道对比与热力图保持同一时间窗：用截取后的首尾工作日
            const params = this.days.length
                ? { date_from: this.days[0], date_to: this.days[this.days.length - 1] }
                : {}
            const { data } = await API.getChannel(params)
            // channel 返回也带 view，但以 heatmap 的 view 为准（同一用户口径一致）
            this.channelCats = data.cats || []
            // 默认把有档的渠道**全部展开**（需求方 2026-07-22 定：档级明细是这段的主要看点，
            // 默认收起等于要用户逐个点开才看得到）。重载后按最新数据重建，不保留旧展开态，
            // 避免渠道/档集合变化后残留错位的 key。无档渠道不入表，isChannelOpen 恒 false。
            const opened = {}
            this.channelCats.forEach(cc => {
                (cc.channels || []).forEach(ch => {
                    if (this.tierList(ch).length) opened[this.chKey(cc, ch)] = true
                })
            })
            this.expandedChannels = opened
        },
        // ===== 渠道占比多维 Phase A：档级展开 =====
        // 该渠道的档级明细。tiers=[] → 无档可展开，不展开、不拿实际派单反推档
        tierList(ch) {
            return Array.isArray(ch.tiers) ? ch.tiers : []
        },
        // 无档（tiers=[]）的两种状态要分开，引导动作不同；tiers 字段整个缺失（旧后端）时都不报，只是不展开
        noTier(ch) {
            return Array.isArray(ch.tiers) && ch.tiers.length === 0
        },
        // B「压根没配、但有实际派单」：渠道级占比 0 + 有真实派单 = 配置缺口。
        // 后端硬卡大类渠道级合计 100%，占比为 0 意味着该渠道根本不在分配里，派单量却真实存在 → 必须去补。
        // config_ratio 严格 === 0（后端恒给 float，0 是真「没配」；不用 falsy，别把 null/undefined 混进来）
        showUnconfigured(ch) {
            return this.noTier(ch) && ch.config_ratio === 0 && ch.actual_num > 0
        },
        // A「配了渠道级、没打标」：设计里「只加渠道不打标」是明确合法路径，后端不校验档级 →
        // 配置本身完整，用户不必做任何事，提示只是告知「还能往下细分」。
        // 边界：config_ratio===0 且 actual_num===0 理论上不会出现（后端 platIds = 配置∪实际），真出现归到 A
        showUntagged(ch) {
            return this.noTier(ch) && !this.showUnconfigured(ch)
        },
        chKey(cc, ch) {
            return cc.category_group_id + '_' + ch.plat_id
        },
        isChannelOpen(cc, ch) {
            return !!this.expandedChannels[this.chKey(cc, ch)]
        },
        toggleChannel(cc, ch) {
            if (!this.tierList(ch).length) return
            this.$set(this.expandedChannels, this.chKey(cc, ch), !this.isChannelOpen(cc, ch))
        },
        // 该大类组下「有档可展开」的渠道（未打标的不算，避免全展开按钮空转）
        catTierChannels(cc) {
            return (cc.channels || []).filter(ch => this.tierList(ch).length)
        },
        isCatAllOpen(cc) {
            const list = this.catTierChannels(cc)
            return list.length > 0 && list.every(ch => this.isChannelOpen(cc, ch))
        },
        toggleCatAll(cc) {
            const open = !this.isCatAllOpen(cc)
            this.catTierChannels(cc).forEach(ch => {
                this.$set(this.expandedChannels, this.chKey(cc, ch), open)
            })
        },
        // 未归类行 config_ratio / config_ratio_abs 为 null → 显 —（0 是「配了但为 0」，要照显）
        hasConf(v) {
            return v !== null && v !== undefined && v !== ''
        },
        // 档行配置占比 tooltip：解释两层口径（渠道内相对 % → 绝对占比）
        tierConfTitle(ch, t) {
            const abs = this.hasConf(t.config_ratio_abs) ? this.fmtRatio(t.config_ratio_abs) + '%' : '—'
            return '渠道内相对占比 ' + this.fmtRatio(t.config_ratio) + '%；'
                + '绝对占比 = 渠道 ' + this.fmtRatio(ch.config_ratio) + '% × 档 ' + this.fmtRatio(t.config_ratio) + '% = ' + abs + '（相对大类组）'
        },
        // 档行实际占比 tooltip：分母是该渠道派单总数，不是大类组总量
        tierActualTitle(ch, t) {
            return '该档 ' + t.actual_num + ' ÷ ' + ch.plat_name + ' 派单总数 ' + ch.actual_num + '（含未归类）'
        },
        // 渠道行实际占比 tooltip：分母是大类组总量
        chActualTitle(cc, ch) {
            return '该渠道 ' + ch.actual_num + ' ÷ 大类组总量 ' + (cc.actual_total === undefined ? '-' : cc.actual_total)
        },
        // 大类某日扩充（创意全量视图，从大类行 days 取）
        catExpand(cat, date) {
            const cell = (cat.days || []).find(d => d.date === date)
            return cell && cell.expand ? cell.expand : 0
        },
        sigClass(signal) {
            return { green: 'hm-green', yellow: 'hm-yellow', red: 'hm-red' }[signal] || 'hm-green'
        },
        pct(ratio) {
            return Math.round((Number(ratio) || 0) * 100)
        },
        fmtRatio(r) {
            const n = Number(r) || 0
            return Number.isInteger(n) ? n : n.toFixed(1)
        },
        barW(r) {
            const n = Math.max(0, Math.min(100, Number(r) || 0))
            return n + '%'
        },
        dowText(d) {
            const map = ['日', '一', '二', '三', '四', '五', '六']
            const wd = new Date(d.replace(/-/g, '/')).getDay()
            return map[wd]
        },
    },
}
</script>

<style scoped lang="scss">
@import "~@/styles/material_sd.scss";

.capacity-board { padding: 4px 0; }

.hm-head { margin-bottom: 12px; line-height: 24px; }
.hm-days { margin-right: 10px; vertical-align: middle; }
.hm-title { font-weight: 600; font-size: 14px; color: #333; margin-right: 10px; }
.hm-mode { display: inline-block; padding: 1px 8px; border-radius: 10px; font-size: 12px; font-weight: 600; margin-right: 8px; }
.hm-mode.full { background: #ecf5ff; color: #409eff; }
.hm-mode.ops { background: #fdf6ec; color: #e6a23c; }
.hm-sub { color: #909399; font-size: 12px; margin-right: 12px; }
.hm-legend .hm-lg { display: inline-block; margin-left: 6px; padding: 1px 7px; border-radius: 8px; font-size: 11px; }
.hm-legend .hm-lg.green { background: #f0f9eb; color: #67c23a; }
.hm-legend .hm-lg.yellow { background: #fdf6ec; color: #e6a23c; }
.hm-legend .hm-lg.red { background: #fef0f0; color: #f56c6c; }
.hm-empty { text-align: center; color: #909399; padding: 40px 0; }

.hm-cat { margin-bottom: 18px; }
.hm-cat-head { font-weight: 600; color: #303133; margin-bottom: 6px; padding-left: 4px; border-left: 3px solid #409eff; }
.hm-scroll { overflow-x: auto; }
.hm-table { border-collapse: collapse; width: 100%; }
.hm-table th, .hm-table td { border: 1px solid #ebeef5; text-align: center; }
.hm-table th { background: #f5f7fa; color: #333; padding: 4px 6px; min-width: 64px; font-weight: 600; }
.hm-grp-th { min-width: 140px; }
.hm-date { font-size: 12px; }
.hm-dow { font-size: 11px; color: #909399; }
.hm-ex { font-size: 11px; color: #67c23a; }
.hm-grp { text-align: left; padding: 4px 8px; background: #fafafa; color: #333; min-width: 140px; }
.hm-grp-leaf { padding-left: 18px; color: #606266; font-weight: normal; }
.hm-cat-row .hm-grp { font-weight: 600; }
.hm-cell { padding: 4px 6px; }
.hm-used { font-size: 13px; font-weight: 600; }
.hm-lim { font-size: 11px; color: #909399; font-weight: normal; }
.hm-expand { color: #E6A23C; font-size: 11px; margin-left: 2px; }
.hm-pct { font-size: 11px; }
.hm-cell.hm-green { background: #f0f9eb; color: #67c23a; }
.hm-cell.hm-yellow { background: #fdf6ec; color: #e6a23c; }
.hm-cell.hm-red { background: #fef0f0; color: #f56c6c; }

.ch-cat { margin-bottom: 16px; }
.ch-cat-head { font-weight: 600; color: #303133; margin-bottom: 6px; padding-left: 4px; border-left: 3px solid #409eff; }
.ch-total { font-weight: normal; color: #909399; font-size: 12px; margin-left: 10px; }
.ch-toggle-all { font-weight: normal; color: #409eff; font-size: 12px; margin-left: 12px; cursor: pointer; }
.ch-toggle-all:hover { color: #66b1ff; }
.ch-table { border-collapse: collapse; width: 100%; max-width: 820px; }
.ch-table th, .ch-table td { border: 1px solid #ebeef5; padding: 6px 10px; font-size: 13px; text-align: left; }
.ch-table th { background: #f5f7fa; color: #333; font-weight: 600; }
.ch-table th:first-child, .ch-table td:first-child { min-width: 210px; }
.ratio-bar { display: inline-block; width: 120px; height: 10px; background: #f0f2f5; border-radius: 5px; overflow: hidden; vertical-align: middle; }
.ratio-bar.sm { width: 92px; height: 8px; }
.ratio-fill { display: block; height: 100%; }
.ratio-fill.conf { background: #409eff; }
.ratio-fill.act { background: #67c23a; }
.ratio-num { margin-left: 8px; font-size: 12px; color: #606266; }
.ratio-na { color: #c0c4cc; font-size: 12px; }

/* 渠道行（可展开）与档级行（从属·缩进+底色+小字号），渠道占比多维 Phase A */
.ch-row-expandable { cursor: pointer; }
.ch-row.ch-row-open td { background: #f8fbff; }
.ch-row.ch-row-expandable:hover td { background: #f0f4f8; }
.ch-caret { display: inline-block; width: 12px; color: #909399; font-size: 12px; margin-right: 4px; vertical-align: middle; }
.ch-caret-none { visibility: hidden; }
.ch-name { vertical-align: middle; }
.ch-tier-count { margin-left: 6px; color: #909399; font-size: 11px; }
/* 无档标记：warn=只是没细分（合法·可选），danger=有派单却没配占比（配置缺口·必须补），配色沿用本页 hm-lg 黄/红 */
.ch-flag { margin-left: 6px; padding: 0 6px; border-radius: 8px; font-size: 11px; cursor: help; }
.ch-flag.warn { background: #fdf6ec; color: #e6a23c; }
.ch-flag.danger { background: #fef0f0; color: #f56c6c; font-weight: 600; }
.ch-tier-row td { background: #fcfcfd; }
.ch-table td.ch-tier-name { position: relative; padding-left: 38px; color: #606266; font-size: 12px; }
.ch-table td.ch-tier-name::before { content: '└'; position: absolute; left: 22px; color: #c0c4cc; }
.ch-tier-row .ratio-num { font-size: 11px; }
.ch-tier-row td:last-child { color: #606266; font-size: 12px; }
</style>
